import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import axios from 'axios';
import LabelText from '../LabelText';
import { getUserFieldValue } from '../../../Util/Util';

const File = (props) => {
	const { t, i18n } = useTranslation();
	const { form_component } = props;
	const { fieldname, file_upload_options, required } = form_component;

	const pageData = useSelector(state => state.pageData);
	const {csrf_token} = pageData;

	const [dzDisplay, setDzDisplay] = useState(true);
	const [uploadResultDisplay, setUploadResultDisplay] = useState(false);
	const [uploadedFile, setUploadedFile] = useState('');
	const [uploadProgress, setUploadProgress] = useState(0);
	const [isUploading, setIsUploading] = useState(false);
	const [acceptedFiles, setAcceptedFiles] = useState([]);
	const [maxSizes, setMaxSizes] = useState({ doc: 0, img: 0, video: 0 });
	const [dzText, setDzText] = useState([]);
	const [files, setFiles] = useState('');

	const fv = useSelector(state => state.fv);
	const userData = useSelector(state => state.userData);
	let userFieldValue = getUserFieldValue(fieldname, userData);
	
	useEffect(() => {
		//console.log('file_upload_options:', file_upload_options);
		
		const newMaxSizes = {};
		const acceptedFilesList = [];
		const dzTextList = [];

		Object.entries(file_upload_options).forEach(([fileType, fileUploadOption]) => {
			if (fileUploadOption.active) {
				let contentTypes = [];
				let text = '';

				switch (fileType) {
					case 'img':
						text = t('圖像 (JPG/JPEG、GIF、PNG)，不能大於 {{size}} MB', { size: fileUploadOption.file_size });
						contentTypes = ['image/jpeg', 'image/gif', 'image/png'];
						break;
					case 'doc':
						text = t('文件 (PDF)，不能大於 {{size}} MB', { size: fileUploadOption.file_size });
						contentTypes = ['application/pdf'];
						break;
					case 'video':
						text = t('影片 (MP4、MOV)，不能大於 {{size}} MB', { size: fileUploadOption.file_size });
						contentTypes = ['video/mp4', 'video/quicktime'];
						break;
				}

				dzTextList.push(<li key={fileType}>{text}</li>);
				acceptedFilesList.push(...contentTypes);
				newMaxSizes[fileType] = parseInt(fileUploadOption.file_size);
			}
		});

		setAcceptedFiles(acceptedFilesList);
		setMaxSizes(newMaxSizes);
		setDzText(dzTextList);
	}, [file_upload_options, i18n.language, t]);

	useEffect(() => {
		if (userData && userData[fieldname]) {
			const userJson = JSON.parse(userData[fieldname])
			//console.log(`userData`, userJson[0]);
			setUploadedFile(userJson[0].client_filename);
			setUploadResultDisplay(true);
			setDzDisplay(false);
			setFiles(userData[fieldname]);
		}
	}, [userData]);

	useEffect(() => { // to be updated after render
		if (fv && required) {
			fv.addField(fieldname, {
				validators: {
					callback: {
						message: t('此欄為必須填寫'),
						callback: function(input) {
							const value = input.value;
							if (!value) return false;
							try {
								const parsed = JSON.parse(value);
								return Array.isArray(parsed) && parsed.length > 0;
							} catch (e) {
								return false;
							}
						}
					}
				}
			});
		}
	}, [fv]);

	const onDrop = useCallback(async (acceptedFiles) => {
		setIsUploading(true);
		acceptedFiles.forEach(async (file) => {
			const fileType = file.type.split('/')[0];
			const maxSize = determineMaxSize(fileType);
			//console.log(`fileType`, fileType, `maxSize`, maxSize);

			if (file.size > maxSize) {
				openAlert(t('上傳之檔案太大'));
				setIsUploading(false);
				return;
			}

			const formData = new FormData();
			formData.append('file', file);
			formData.append('_csrfToken', csrf_token);

			try {
				const response = await axios.post(`/TmpFiles/ajaxUpload`, formData, {
					onUploadProgress: (progressEvent) => {
						const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
						setUploadProgress(percentCompleted);
					}
				});
				if (response.status === 200) {
					const fileData = JSON.stringify([response.data]);
					setUploadedFile(file.name);
					setUploadResultDisplay(true);
					setDzDisplay(false);
					setFiles(fileData);
					// 直接更新 hidden input 的值並觸發 FormValidation 重新驗證
					if (fv) {
						setTimeout(() => {
							const hiddenInput = document.getElementById(fieldname);
							if (hiddenInput) {
								hiddenInput.value = fileData;
								fv.revalidateField(fieldname);
							}
						}, 100);
					}
					console.log('File uploaded successfully');
				} else {
					console.error('File upload failed');
				}
			} catch (error) {
				console.error('Error uploading file:', error);
			} finally {
				setIsUploading(false);
			}
		});
	}, [csrf_token, t, maxSizes]);

	const determineMaxSize = (fileType) => {
		//console.log(`maxSizes in determineMaxSize`, maxSizes);
    	switch (fileType) {
	        case 'image':
				console.log(`maxSizes.img`, maxSizes.img);
	            return maxSizes.img * 1024 * 1024; // Convert MB to bytes
	        case 'application':
	            return maxSizes.doc * 1024 * 1024; // Convert MB to bytes
	        case 'video':
	            return maxSizes.video * 1024 * 1024; // Convert MB to bytes
	        default:
	            return Math.max(...Object.values(maxSizes)) * 1024 * 1024; // Fallback to max value
	    }
	};

	const onDropRejected = useCallback((rejectedFiles) => {
		rejectedFiles.forEach(file => {
			const errorMessages = file.errors.map(error => {
				switch (error.code) {
					case 'file-too-large':
						return t('上傳之檔案太大');
					case 'file-invalid-type':
						return t('檔案類別無效');
					default:
						return error.message;
				}
			});
			openAlert(errorMessages.join(', '));
			setUploadResultDisplay(false);
			setIsUploading(false);
			setDzDisplay(true);
		});
	}, [t]);

	const { getRootProps, getInputProps } = useDropzone({
		accept: acceptedFiles.reduce((acc, type) => ({ ...acc, [type]: [] }), {}),
		onDrop,
		onDropRejected
	});

	const reUpload = () => {
		setUploadResultDisplay(false);
		setDzDisplay(true);
		setFiles('');
		// 直接更新 hidden input 的值並觸發 FormValidation 重新驗證
		if (fv) {
			setTimeout(() => {
				const hiddenInput = document.getElementById(fieldname);
				if (hiddenInput) {
					hiddenInput.value = '';
					fv.revalidateField(fieldname);
				}
			}, 100);
		}
	};

	return (
		<div className={`flex-grow-1 form-group text ${required ? 'required' : ''}`}>
			<input type="hidden" name={fieldname} id={fieldname} value={userFieldValue || files} />
			<LabelText
				form_component={form_component}
			/>
			{dzDisplay && (
				<>
					{!isUploading && (
						<div {...getRootProps()}>
							<input {...getInputProps()} />
							<div className="dropzone mw-500px">
								<div className="dz-message needsclick">
									<i className="bi bi-file-earmark-arrow-up text-primary fs-3x"></i>
									<div className="ms-4 text-start">
										{t('支援檔案格式：')}
										<ul>
											{dzText}
										</ul>
									</div>
								</div>
							</div>
						</div>
					)}
					{isUploading && (
						<div className="progress mw-500px">
							<div
								className="progress-bar"
								role="progressbar"
								style={{ width: `${uploadProgress}%` }}
								aria-valuenow={uploadProgress}
								aria-valuemin="0"
								aria-valuemax="100"
							></div>
						</div>
					)}
				</>
			)}
			{uploadResultDisplay && (
				<div className="d-flex align-items-center gap-3 uploaded flex-wrap">
					<div className="col-12 d-flex gap-3">
						<div className="flex-grow-1">
							<p className="me-3 mb-0 text-muted">{t('已上傳的檔案：')}</p>
							<div className="d-flex align-items-center gap-2">
								{(() => {
									const fileData = files ? JSON.parse(files)[0] : null;
									const fileType = fileData ? (fileData.mime_type || fileData.type || fileData.content_type) : '';
									
									if (fileType && fileType.startsWith('image/')) {
										return <i className="bi bi-file-earmark-image text-success fs-4"></i>;
									} else if (fileType === 'application/pdf') {
										return <i className="bi bi-file-earmark-pdf text-danger fs-4"></i>;
									} else if (fileType && fileType.startsWith('video/')) {
										return <i className="bi bi-file-earmark-play text-primary fs-4"></i>;
									} else {
										return <i className="bi bi-file-earmark text-secondary fs-4"></i>;
									}
								})()}
								<span className="fw-bolder text-dark fs-5">{uploadedFile}</span>
							</div>
						</div>
						<div classN="flex-shrink-0 d-flex align-items-center">
							<button
								type="button"
								onClick={reUpload}
								className="btn btn-sm btn-primary rounded-circle p-3 d-flex flex-center shadow-xs"
							>
								<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
									<path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" fill="currentcolor"/>
									<path fill-rule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" fill="currentcolor"/>
								</svg>
							</button>
						</div>
					</div>
					
					{(() => {
						const fileData = files ? JSON.parse(files)[0] : null;
						const fileType = fileData ? (fileData.mime_type || fileData.type || fileData.content_type) : '';
						
						if (fileType && fileType.startsWith('image/')) {
							const imageUrl = fileData.file_url || fileData.url || fileData.path;
							return (
								<div className="upload-preview position-relative col-12 mw-300px">
									<img src={imageUrl} alt={uploadedFile} className="w-100 h-auto" />
								</div>
							);
						}
						return null;
					})()}
				</div>
			)}
		</div>
	);
}

export default File;